/**
 * ルーム関連スキーマ
 *
 * ルーム設定、参加者、ゲーム状態のスキーマ
 */

import { z } from "zod";
import { createParticipantId, type ParticipantId } from "../types";
import { BallCountSchema, BingoNumberSchema, ThemeIdSchema } from "./base";

// ============================================
// ルーム設定
// ============================================

/** ルーム設定スキーマ（完全版） */
export const RoomSettingsSchema = z.object({
  maxParticipants: z.number().int().min(2).max(500),
  autoMarkEnabled: z.boolean(),
  theme: ThemeIdSchema,
  allowLateJoin: z.boolean(),
  bingoGoal: z.number().int().min(0).max(100),
  ballCount: BallCountSchema,
});

/** ルーム設定スキーマ（部分更新用） */
export const PartialRoomSettingsSchema = RoomSettingsSchema.partial();

// ============================================
// ビンゴカード・勝利ライン
// ============================================

/** ビンゴカードスキーマ */
export const BingoCardSchema = z.object({
  id: z.string(),
  grid: z.array(z.union([BingoNumberSchema, z.literal("FREE")])),
  markedIndices: z.array(z.number()),
  themeId: z.string(),
});

/** 勝利ラインスキーマ */
export const WinningLineSchema = z.object({
  indices: z.array(z.number()),
  type: z.enum(["row", "column", "diagonal"]),
});

// ============================================
// ホスト・参加者
// ============================================

/** ホストスキーマ */
export const HostSchema = z.object({
  id: z.string(),
  nickname: z.string(),
  isConnected: z.boolean(),
});

/** 参加者スキーマ */
export const ParticipantSchema = z.object({
  id: z.string(),
  nickname: z.string(),
  card: BingoCardSchema,
  joinedAt: z.string(),
  bingoCount: z.number(),
  isConnected: z.boolean(),
});

// ============================================
// ゲーム状態
// ============================================

/** ゲームスキーマ */
export const GameSchema = z.object({
  drawnNumbers: z.array(BingoNumberSchema),
  currentNumber: BingoNumberSchema.nullable(),
  winners: z.array(z.string()),
  startedAt: z.string().nullable(),
  finishedAt: z.string().nullable(),
});

/** ゲームイベントログスキーマ */
export const GameEventLogSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("CARD_MARKED"),
    timestamp: z.string(),
    participantId: z.string(),
    nickname: z.string(),
    number: BingoNumberSchema,
    drawCount: z.number(),
  }),
  z.object({
    type: z.literal("REACH_ACHIEVED"),
    timestamp: z.string(),
    participantId: z.string(),
    nickname: z.string(),
    reachCount: z.number(),
    drawCount: z.number(),
  }),
  z.object({
    type: z.literal("BINGO_ACHIEVED"),
    timestamp: z.string(),
    participantId: z.string(),
    nickname: z.string(),
    bingoCount: z.number(),
    drawCount: z.number(),
  }),
]);

// ============================================
// WebSocket セッション
// ============================================

/** WebSocket セッション内部スキーマ */
const WebSocketSessionBaseSchema = z.object({
  participantId: z.string(),
  role: z.enum(["host", "player"]),
  connectedAt: z.string(),
});

/** WebSocket セッションスキーマ（ParticipantIdに変換） */
export const WebSocketSessionSchema = WebSocketSessionBaseSchema.transform(
  (data): { participantId: ParticipantId; role: "host" | "player"; connectedAt: string } => ({
    ...data,
    participantId: createParticipantId(data.participantId),
  }),
);

/** WebSocket セッション型 */
export type WebSocketSession = z.infer<typeof WebSocketSessionSchema>;
